import * as React from "react";
import { useEffect, useState } from "react";
import axios from "axios";
import { useHistory, useParams } from "react-router-dom";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { Notification, useNotify } from "react-admin";
import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPost } from "../getConfig";
import { brandLabel, useBrandNames } from "./brands";
import {
  DataTable,
  FilterSelect,
  PageHeader,
  SectionHeader,
  StatCard,
  StatusChip,
  errorMessage,
  fmtDateTime,
  gbp,
  humanize,
  useCloStyles,
  walletTotal,
} from "./shared";

const INFLOWS = ["credit", "payout_failed_restore", "manual_credit"];

const ENTRY_COLUMNS = [
  { label: "ID" },
  { label: "When" },
  { label: "Type" },
  { label: "Amount", numeric: true },
  { label: "Reason" },
  { label: "Billing line" },
  { label: "Reference" },
  { label: "By" },
];
const NOTICE_COLUMNS = [{ label: "Sent" }, { label: "Stage" }, { label: "For" }, { label: "Balance", numeric: true }, { label: "Delivery" }];
const BREAKAGE_COLUMNS = [{ label: "When" }, { label: "Reason" }, { label: "Brand" }, { label: "Amount", numeric: true }, { label: "Retailer billed" }];
const ADJUSTMENT_COLUMNS = [
  { label: "Requested" },
  { label: "Direction" },
  { label: "Amount", numeric: true },
  { label: "Reason" },
  { label: "Status" },
  { label: "Requested by" },
  { label: "Decided by" },
];

const DIRECTIONS = [
  { value: "credit", label: "Credit (add to wallet)" },
  { value: "debit", label: "Debit (take from wallet)" },
];

type Request = { direction: string; pounds: string; reason: string };
const EMPTY_REQUEST: Request = { direction: "credit", pounds: "", reason: "" };

const signed = (e: any) => (INFLOWS.includes(e.type) ? gbp(e.amount_minor) : `−${gbp(e.amount_minor)}`);

const WalletShow = () => {
  const classes = useCloStyles();
  const notify = useNotify();
  const history = useHistory();
  const { userId } = useParams<{ userId: string }>();
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<any>(null);
  const [requesting, setRequesting] = useState(false);
  const [request, setRequest] = useState<Request>(EMPTY_REQUEST);
  const [saving, setSaving] = useState(false);
  const names = useBrandNames((detail?.breakage || []).map((b: any) => b.brand_id));

  const load = async () => {
    try {
      setLoading(true);
      const res: any = await axios.get(`${ENDPOINTS.CLO_WALLETS}/${userId}`, getConfigGet({}));
      setDetail(res.data);
    } catch (err) {
      notify(errorMessage(err, "Failed to load wallet"), "warning");
      setDetail(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    const amountMinor = Math.round(Number(request.pounds) * 100);
    const reason = request.reason.trim();
    if (!Number.isFinite(amountMinor) || amountMinor < 1) {
      notify("Enter an amount of at least £0.01", "warning");
      return;
    }
    if (!reason) {
      notify("A reason is required", "warning");
      return;
    }
    try {
      setSaving(true);
      await axios.post(
        ENDPOINTS.CLO_WALLET_ADJUSTMENTS,
        { userId: Number(userId), direction: request.direction, amountMinor, reason },
        { headers: getConfigPost() }
      );
      notify("Adjustment requested. A super admin must approve it in Adjustments.", "info");
      setRequesting(false);
      setRequest(EMPTY_REQUEST);
      load();
    } catch (err) {
      notify(errorMessage(err, "Failed to request adjustment"), "warning");
    } finally {
      setSaving(false);
    }
  };

  const back = (
    <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => history.push("/clo/wallets")}>
      Wallets
    </Button>
  );

  if (loading) {
    return <div className={classes.loadingContainer}><CircularProgress size={40} /></div>;
  }
  if (!detail) {
    return (
      <div className={classes.main}>
        <PageHeader title={`User ${userId}`} subtitle="No CLO wallet found for this customer." actions={back} />
        <Notification />
      </div>
    );
  }

  const { wallet, entries, notices, breakage, adjustments } = detail;

  return (
    <div className={classes.main}>
      <PageHeader
        title={<>User {wallet.user_id} <StatusChip status={wallet.status} /></>}
        subtitle="Read-only history for complaints handling. Corrections go through a dual-authorised adjustment."
        actions={
          <>
            {back}
            <Button
              variant="contained"
              color="primary"
              disableElevation
              disabled={wallet.status !== "active"}
              onClick={() => setRequesting(true)}
            >
              Request adjustment
            </Button>
          </>
        }
      />

      <div className={classes.statRow}>
        <StatCard label="Available" value={gbp(walletTotal(wallet))} />
        <StatCard label="Unswept" value={gbp(wallet.available_minor)} />
        <StatCard label="In e-wallet, not yet paid to lender" value={gbp(wallet.swept_unspent_minor)} />
        <StatCard label="Last app session" value={<Typography variant="body1">{fmtDateTime(wallet.last_app_session_at)}</Typography>} />
        <StatCard label="Last credit" value={<Typography variant="body1">{fmtDateTime(wallet.last_credit_at)}</Typography>} />
        <StatCard label="Opened" value={<Typography variant="body1">{fmtDateTime(wallet.createdAt)}</Typography>} />
        {wallet.closed_at && <StatCard label="Closed" value={<Typography variant="body1">{fmtDateTime(wallet.closed_at)}</Typography>} />}
      </div>

      <SectionHeader title="Ledger" meta={`${entries.length} most recent entries`} />
      <DataTable columns={ENTRY_COLUMNS} isEmpty={entries.length === 0} empty="No entries yet.">
        {entries.map((e: any) => (
          <TableRow key={e.id} hover>
            <TableCell>{e.id}</TableCell>
            <TableCell>{fmtDateTime(e.createdAt)}</TableCell>
            <TableCell>{humanize(e.type)}</TableCell>
            <TableCell align="right"><strong>{signed(e)}</strong></TableCell>
            <TableCell>{humanize(e.reason)}</TableCell>
            <TableCell>{e.reward_billing_id || "—"}</TableCell>
            <TableCell>{e.arn || e.transfer_ref || "—"}</TableCell>
            <TableCell>{e.created_by}</TableCell>
          </TableRow>
        ))}
      </DataTable>

      <SectionHeader title="Breakage" meta="Money kept by Sprive instead of credited" />
      <DataTable columns={BREAKAGE_COLUMNS} isEmpty={breakage.length === 0} empty="Nothing broken or forfeited.">
        {breakage.map((b: any) => (
          <TableRow key={b.id} hover>
            <TableCell>{fmtDateTime(b.createdAt)}</TableCell>
            <TableCell>{humanize(b.reason)}</TableCell>
            <TableCell>{b.brand_id ? brandLabel(names, b.brand_id) : "Whole balance"}</TableCell>
            <TableCell align="right"><strong>{gbp(b.amount_minor)}</strong></TableCell>
            <TableCell>{b.billed_to_retailer ? "Yes" : "No"}</TableCell>
          </TableRow>
        ))}
      </DataTable>

      <SectionHeader title="Notices" meta="Inactivity and bank-connection reminders" />
      <DataTable columns={NOTICE_COLUMNS} isEmpty={notices.length === 0} empty="No notices sent.">
        {notices.map((n: any) => (
          <TableRow key={n.id} hover>
            <TableCell>{fmtDateTime(n.createdAt)}</TableCell>
            <TableCell>{humanize(n.stage)}</TableCell>
            <TableCell>{fmtDateTime(n.anchor_at)}</TableCell>
            <TableCell align="right">{gbp(n.balance_at_notice_minor)}</TableCell>
            <TableCell><StatusChip status={n.delivery_status} /></TableCell>
          </TableRow>
        ))}
      </DataTable>

      <SectionHeader title="Adjustments" />
      <DataTable columns={ADJUSTMENT_COLUMNS} isEmpty={adjustments.length === 0} empty="No adjustments.">
        {adjustments.map((a: any) => (
          <TableRow key={a.id} hover>
            <TableCell>{fmtDateTime(a.createdAt)}</TableCell>
            <TableCell>{humanize(a.direction)}</TableCell>
            <TableCell align="right"><strong>{gbp(a.amount_minor)}</strong></TableCell>
            <TableCell>{a.reason}</TableCell>
            <TableCell><StatusChip status={a.status} /></TableCell>
            <TableCell>{a.requested_by}</TableCell>
            <TableCell>{a.decided_by || "—"}</TableCell>
          </TableRow>
        ))}
      </DataTable>

      <Dialog open={requesting} onClose={() => !saving && setRequesting(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request adjustment</DialogTitle>
        <DialogContent>
          <div className={classes.dialogContent}>
            <Typography variant="body2" className={classes.muted}>
              User {wallet.user_id} · {gbp(wallet.available_minor)} unswept (the most a debit can take). Nothing changes until a different super admin approves it.
            </Typography>
            <FilterSelect
              label="Direction"
              value={request.direction}
              onChange={(direction) => setRequest({ ...request, direction })}
              options={DIRECTIONS}
              allLabel={null}
              fullWidth
            />
            <TextField
              variant="outlined"
              size="small"
              label="Amount (£)"
              type="number"
              inputProps={{ min: 0.01, max: 1000, step: 0.01 }}
              value={request.pounds}
              onChange={(e) => setRequest({ ...request, pounds: e.target.value })}
              required
            />
            <TextField
              variant="outlined"
              size="small"
              label="Reason"
              value={request.reason}
              onChange={(e) => setRequest({ ...request, reason: e.target.value })}
              multiline
              rows={3}
              inputProps={{ maxLength: 255 }}
              helperText={`${request.reason.length}/255 · e.g. complaint reference`}
              required
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequesting(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} color="primary" variant="contained" disableElevation disabled={saving}>
            {saving ? <CircularProgress size={20} /> : "Request"}
          </Button>
        </DialogActions>
      </Dialog>

      <Notification />
    </div>
  );
};

export default WalletShow;
